import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  ActivityEntry, AttendanceOverview, DashboardSummary, DepartmentHeadcount,
  EmployeeTrend, LeaveSummary, PayrollSummary, RecruitmentSummary, UpcomingEvent,
} from '../types'

const BASE = '/v1/dashboard'

/**
 * One hook per widget rather than one call for the whole page.
 *
 * Each card then renders the moment its own data lands, so a slow panel never holds up a
 * fast one — and a user who lacks, say, `payroll:read` simply sees that one card fail
 * rather than losing the entire dashboard to a 403.
 *
 * `staleTime` matches the backend's 60-second Redis TTL: refetching sooner would only
 * return the same cached response.
 */
const DASHBOARD_STALE_TIME = 60_000

function useDashboardQuery<T>(key: unknown[], path: string, params?: Record<string, unknown>) {
  return useQuery({
    queryKey: ['dashboard', ...key],
    queryFn: async () => {
      const { data } = await apiClient.get<T>(`${BASE}${path}`, { params })
      return data
    },
    staleTime: DASHBOARD_STALE_TIME,
  })
}

export function useSummary(date?: string) {
  return useDashboardQuery<DashboardSummary>(['summary', date], '/summary', { date })
}

export function useAttendanceOverview(date?: string) {
  return useDashboardQuery<AttendanceOverview>(['attendance', date], '/attendance-overview', { date })
}

export function useEmployeeTrend(months: 6 | 12 = 6) {
  return useDashboardQuery<EmployeeTrend>(['trend', months], '/employee-trend', { months })
}

export function useDepartmentHeadcount() {
  return useDashboardQuery<DepartmentHeadcount[]>(['departments'], '/department-headcount')
}

export function useRecentActivities(limit = 6) {
  return useDashboardQuery<ActivityEntry[]>(['activities', limit], '/recent-activities', { limit })
}

export function useUpcomingEvents(limit = 4) {
  return useDashboardQuery<UpcomingEvent[]>(['events', limit], '/upcoming-events', { limit })
}

export function useLeaveSummary(period?: string) {
  return useDashboardQuery<LeaveSummary>(['leave', period], '/leave-summary', { period })
}

export function usePayrollSummary(period?: string) {
  return useDashboardQuery<PayrollSummary>(['payroll', period], '/payroll-summary', { period })
}

export function useRecruitmentSummary(period?: string) {
  return useDashboardQuery<RecruitmentSummary>(['recruitment', period], '/recruitment-summary', { period })
}
